/**
 * Vervit — gift wrap add-to-cart (main + gift wrap in one Shopify /cart/add.js call).
 */
(function () {
  'use strict';

  function getConfig() {
    return window.vrvGiftWrapConfig && typeof window.vrvGiftWrapConfig === 'object'
      ? window.vrvGiftWrapConfig
      : {};
  }

  function queryRoot(root) {
    if (!root) return document;
    if (root.querySelector) return root;
    if (root instanceof Element) return root;
    return document;
  }

  function getGiftWrapVariantId(root) {
    const cfg = getConfig();
    const scope = queryRoot(root);
    const input = scope.querySelector('[data-vrv-gift-wrap-variant]');
    if (input && input.value) return String(input.value).trim();
    if (cfg.variantId) return String(cfg.variantId).trim();
    return '';
  }

  function isGiftWrapChecked(root) {
    const scope = queryRoot(root);
    const checkbox = scope.querySelector('[data-vrv-gift-wrap]');
    return Boolean(checkbox && checkbox.checked && !checkbox.disabled);
  }

  function shouldIncludeGiftWrap(root, override) {
    if (override === false) return false;
    const variantId = getGiftWrapVariantId(root);
    if (!variantId) return false;
    if (override === true) return true;
    return isGiftWrapChecked(root);
  }

  function parseMainItemFromFormData(formData) {
    if (!formData || typeof formData.get !== 'function') return null;
    const id = Number(formData.get('id'));
    if (!id) return null;

    const item = {
      id,
      quantity: Math.max(1, parseInt(formData.get('quantity'), 10) || 1),
    };

    const properties = {};
    formData.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(String(key));
      if (match && match[1] && value != null && String(value).trim() !== '') {
        properties[match[1]] = value;
      }
    });
    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    return item;
  }

  function normalizeLineItem(item) {
    if (!item || !item.id) return null;
    const line = {
      id: Number(item.id),
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
    };
    if (item.properties && typeof item.properties === 'object' && Object.keys(item.properties).length) {
      line.properties = item.properties;
    }
    if (item.selling_plan) line.selling_plan = Number(item.selling_plan);
    return line;
  }

  function createGiftWrapGroup() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'vrv-gw-' + window.crypto.randomUUID();
    }
    return 'vrv-gw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function buildGiftWrapLine(variantId, association) {
    const id = Number(variantId || getGiftWrapVariantId());
    if (!id) return null;
    const properties = { VrvGiftWrap: 'yes' };
    if (association && association.group) properties._VrvGiftWrapGroup = String(association.group);
    if (association && association.parentVariantId) {
      properties._VrvGiftWrapParentVariant = String(association.parentVariantId);
    }
    if (association && association.parentKey) properties._VrvGiftWrapParentKey = String(association.parentKey);
    return {
      id,
      quantity: 1,
      properties,
    };
  }

  function getCartElement(explicit) {
    if (explicit !== undefined && explicit !== null) return explicit;
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getSectionPayload(cartEl) {
    if (!cartEl) return {};
    const helpers = window.vrvCartAddHelpers || {};
    let sectionIds = [];

    if (typeof window.vrvGetCartAddSections === 'function') {
      sectionIds = window.vrvGetCartAddSections();
    } else if (typeof helpers.getCartAddSectionIds === 'function') {
      sectionIds = helpers.getCartAddSectionIds(cartEl);
    } else if (typeof cartEl.getSectionsToRender === 'function') {
      sectionIds = cartEl.getSectionsToRender().map((s) => s.id || s.section);
    }

    if (!sectionIds.length) return {};

    if (typeof cartEl.setActiveElement === 'function') {
      cartEl.setActiveElement(document.activeElement);
    }

    return {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  function getNativeFetch() {
    return (
      window.__vrvTrueNativeFetch ||
      window.__vrvNativeFetch ||
      (typeof window.fetch === 'function' ? window.fetch.bind(window) : null)
    );
  }

  function getCartAddJsUrl() {
    const raw = (window.routes && window.routes.cart_add_url) || '/cart/add';
    if (/\.js(?:\?|$)/.test(String(raw))) return raw;
    return String(raw).replace(/\/$/, '') + '.js';
  }

  function postCartAddJson(body) {
    const url = getCartAddJsUrl();
    const fn = getNativeFetch();
    if (!fn) return Promise.reject(new Error('fetch unavailable'));

    return fn(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => ({
          ok: response.ok,
          status: response.status,
          data: data && typeof data === 'object' ? data : {},
        }))
    );
  }

  function postCartAddFormData(formData) {
    const helpers = window.vrvCartAddHelpers || {};
    if (typeof helpers.fetchCartAdd === 'function') {
      return helpers.fetchCartAdd(formData).then(({ ok, data }) => ({
        ok,
        status: ok ? 200 : 422,
        data: data || {},
      }));
    }
    return postCartAddJson(Object.fromEntries(formData.entries()));
  }

  function isSuccess(result) {
    const helpers = window.vrvCartAddHelpers || {};
    const ok = result && result.ok;
    const data = (result && result.data) || {};
    if (typeof helpers.isCartAddSuccess === 'function') {
      return helpers.isCartAddSuccess(data, ok);
    }
    if (typeof helpers.isCartAddError === 'function') {
      return !helpers.isCartAddError(data, ok);
    }
    return Boolean(ok && (data.items || data.id || data.key || data.sections));
  }

  function logFailure(label, result, extra) {
    console.warn('[VRV gift wrap cart] ' + label, Object.assign(
      {
        httpStatus: result && result.status,
        shopifyMessage: result && result.data && (result.data.description || result.data.message),
        shopifyErrors: result && result.data && (result.data.errors || result.data.items),
      },
      extra || {}
    ));
  }

  function finalizeResult(result, includeGift, giftAdded) {
    const data = (result && result.data) || {};
    return {
      ok: isSuccess(result),
      response: data,
      includeGiftWrap: includeGift,
      giftWrapAdded: Boolean(giftAdded),
      giftWrapFailed: Boolean(includeGift && !giftAdded && isSuccess(result)),
    };
  }

  function addGiftWrapLineOnly(giftLine, cartEl, skipSections) {
    const body = Object.assign({ items: [giftLine] }, skipSections ? {} : getSectionPayload(cartEl));
    return postCartAddJson(body);
  }

  /**
   * Add main product + optional gift wrap.
   * Prefers ONE /cart/add.js JSON request with items: [main, giftWrap].
   */
  function addMainAndGiftWrap(options) {
    const opts = options || {};
    const root = opts.root || document;
    const includeGift = shouldIncludeGiftWrap(root, opts.includeGiftWrap);
    const giftVariantId = opts.giftVariantId
      ? String(opts.giftVariantId).trim()
      : getGiftWrapVariantId(root);
    const skipSections = Boolean(opts.skipSections);
    const cartEl = skipSections ? null : getCartElement(opts.cartEl);

    let mainItem = normalizeLineItem(opts.mainItem);
    if (!mainItem && opts.formData) {
      mainItem = normalizeLineItem(parseMainItemFromFormData(opts.formData));
    }
    if (!mainItem) {
      return Promise.reject(new Error('Please select a product option before adding to cart.'));
    }

    // The gift wrap is tied to the product by its variant id (not a per-add
    // unique group): a unique group made every "add with gift wrap" a brand-new
    // cart line next to the plain one, i.e. a duplicate product entry.
    let giftLine = includeGift ? buildGiftWrapLine(giftVariantId, { parentVariantId: mainItem.id }) : null;
    if (includeGift && !giftLine) {
      return Promise.reject(
        new Error('Gift wrap is not configured. Please contact the store or try without gift wrap.')
      );
    }

    const inspect = includeGift
      ? fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      : Promise.resolve(null);

    return inspect.then((currentCart) => {
      let alreadyWrapped = false;
      if (currentCart && Array.isArray(currentCart.items)) {
        // Merge into the existing line of this product when it only differs by
        // our own bookkeeping properties.
        const existing = currentCart.items.find(
          (line) =>
            !isGiftWrapCartItem(line) &&
            Number(line.variant_id) === Number(mainItem.id) &&
            sameUserProperties(line.properties, mainItem.properties)
        );
        if (existing && existing.properties && Object.keys(existing.properties).length) {
          mainItem.properties = Object.assign({}, existing.properties);
        }
        alreadyWrapped = cartHasGiftWrapFor(currentCart, mainItem.id);
      }
      if (alreadyWrapped) giftLine = null;
      return submitMainAndGift(giftLine, alreadyWrapped);
    });

    function submitMainAndGift(giftLine, alreadyWrapped) {
    const items = giftLine ? [mainItem, giftLine] : [mainItem];
    const body = Object.assign({ items }, skipSections ? {} : getSectionPayload(cartEl));

    return postCartAddJson(body).then((result) => {
      if (isSuccess(result)) {
        return finalizeResult(result, includeGift, Boolean(giftLine) || Boolean(alreadyWrapped));
      }

      logFailure('combined add failed', result, { items });

      if (!giftLine) {
        // Shopify already gave a definitive validation answer (e.g. stock limit
        // reached); replaying the same add as form data can only fail the same
        // way, so don't spend a second round trip on it.
        const definitive =
          result && result.data && Number(result.data.status) === 422 && (result.data.description || result.data.message);
        if (opts.formData && !definitive) {
          return postCartAddFormData(opts.formData).then((formResult) => finalizeResult(formResult, false, false));
        }
        return finalizeResult(result, false, false);
      }

      // Fallback: main first, then gift wrap (legacy path).
      const mainOnlyBody = Object.assign({ items: [mainItem] }, skipSections ? {} : getSectionPayload(cartEl));
      return postCartAddJson(mainOnlyBody).then((mainResult) => {
        if (!isSuccess(mainResult)) {
          return finalizeResult(mainResult, includeGift, false);
        }
        return addGiftWrapLineOnly(giftLine, cartEl, skipSections).then((giftResult) => {
          if (!isSuccess(giftResult)) {
            logFailure('gift wrap fallback add failed', giftResult, { giftVariantId });
            return Object.assign(finalizeResult(mainResult, true, false), {
              giftWrapFailed: true,
              giftWrapError: giftResult.data,
            });
          }
          return finalizeResult(giftResult, true, true);
        });
      });
    });
    }
  }

  /** Buy Now — empty cart then add main + gift wrap atomically. */
  function addBuyNowLines(mainItem, giftVariantId) {
    const main = normalizeLineItem(mainItem);
    if (!main) {
      return Promise.reject(new Error('Could not add product for Buy Now checkout. Please try again.'));
    }

    const giftId = giftVariantId ? String(giftVariantId).trim() : '';
    const items = [main];
    const giftLine = giftId ? buildGiftWrapLine(giftId, { parentVariantId: main.id }) : null;
    if (giftLine) items.push(giftLine);

    return postCartAddJson({ items }).then((result) => {
      if (isSuccess(result)) {
        return { ok: true, data: result.data, cart: result.data };
      }
      logFailure('buy now add failed', result, { items });
      const detail = result.data && (result.data.description || result.data.message);
      if (detail && /stock|available|only\s+\d+|all\s+\d+|inventory|sold out/i.test(detail)) {
        throw new Error("You can't add more than the available stock quantity.");
      }
      throw new Error(detail || 'Could not add product for Buy Now checkout. Please try again.');
    });
  }

  /**
   * Add the gift-wrap product on its own (not alongside a main product) —
   * used by the standalone "Add" button in the cart drawer's gift section.
   * Reuses the same buildGiftWrapLine()/addGiftWrapLineOnly() the PDP
   * checkbox flow already relies on, so it gets the exact same VrvGiftWrap
   * line-item property (and therefore the same quantity-lock and
   * discount-exclusion handling already built for that line everywhere else
   * in the theme) for free.
   */
  function addGiftWrapOnly(options) {
    const opts = options || {};
    const giftVariantId = opts.giftVariantId
      ? String(opts.giftVariantId).trim()
      : getGiftWrapVariantId();
    const baseGiftLine = buildGiftWrapLine(giftVariantId);
    if (!baseGiftLine) {
      return Promise.reject(
        new Error('Gift wrap is not configured. Please contact the store.')
      );
    }
    const cartEl = getCartElement(opts.cartEl);
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => response.json())
      .then((cart) => {
        const mains = (cart.items || []).filter((item) => !isGiftWrapCartItem(item));
        const parent = mains[0];
        if (!parent) throw new Error('Add a product before adding Gift Wrap.');
        // A wrap that still has its product in the cart already exists: keep it
        // instead of adding a second, duplicate Gift Wrap line.
        const hasWrap = (cart.items || []).some(
          (item) => isGiftWrapCartItem(item) && giftWrapHasParent(item, mains)
        );
        if (hasWrap) return { ok: true, data: cart, status: 200, alreadyPresent: true };
        const giftLine = buildGiftWrapLine(giftVariantId, {
          parentVariantId: parent.variant_id
        });
        return addGiftWrapLineOnly(giftLine, cartEl, Boolean(opts.skipSections));
      }).then((result) => {
      if (result && result.alreadyPresent) return result.data;
      if (!isSuccess(result)) {
        logFailure('standalone gift add failed', result, { giftVariantId });
        throw new Error(
          (result.data && (result.data.description || result.data.message)) ||
            'Could not add gift to cart. Please try again.'
        );
      }
      return result.data;
    });
  }

  window.vrvGiftWrapCart = {
    getConfig,
    getGiftWrapVariantId,
    isGiftWrapChecked,
    shouldIncludeGiftWrap,
    parseMainItemFromFormData,
    buildGiftWrapLine,
    addMainAndGiftWrap,
    addBuyNowLines,
    addGiftWrapOnly,
  };

  // Properties a shopper/PDP can set, ignoring our own "_VrvGiftWrap*" bookkeeping.
  function userProps(properties) {
    const out = {};
    Object.keys(properties || {}).forEach((key) => {
      if (key.indexOf('_VrvGiftWrap') === 0) return;
      const value = properties[key];
      if (value != null && String(value) !== '') out[key] = String(value);
    });
    return out;
  }

  function sameUserProperties(a, b) {
    return JSON.stringify(Object.keys(userProps(a)).sort().map((k) => [k, userProps(a)[k]])) ===
      JSON.stringify(Object.keys(userProps(b)).sort().map((k) => [k, userProps(b)[k]]));
  }

  function cartHasGiftWrapFor(cart, variantId) {
    const items = (cart && cart.items) || [];
    const mains = items.filter((item) => !isGiftWrapCartItem(item));
    return items.some((item) => {
      if (!isGiftWrapCartItem(item)) return false;
      const p = item.properties || {};
      if (Number(p._VrvGiftWrapParentVariant) === Number(variantId)) return true;
      const group = String(p._VrvGiftWrapGroup || '');
      return Boolean(
        group &&
          mains.some(
            (m) =>
              Number(m.variant_id) === Number(variantId) &&
              String((m.properties || {})._VrvGiftWrapGroup || '') === group
          )
      );
    });
  }

  function isGiftWrapCartItem(item) {
    if (!item) return false;
    if (item.properties && item.properties.VrvGiftWrap != null) return true;
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const title = String(item.product_title || item.title || '').toLowerCase();
    return handle.includes('gift-wrap') || handle.includes('gift_wrap') || title.includes('gift wrap');
  }

  let giftDependencySyncPromise = null;
  let giftDependencySyncRequested = false;
  let giftDependencyLatestCart = null;
  let giftDependencyRepairVersion = 0;

  function giftWrapHasParent(gift, mainItems) {
    const properties = gift.properties || {};
    const group = String(properties._VrvGiftWrapGroup || '');
    const parentKey = String(properties._VrvGiftWrapParentKey || '');
    const parentVariant = Number(properties._VrvGiftWrapParentVariant || 0);

    // A cart line's `key` changes whenever that line's properties change
    // (e.g. a combo/discount script touching the parent line), even though
    // the same product is still in the cart. Checking group/key/variant as
    // independent OR conditions -- instead of returning on whichever
    // identifier happens to be set first -- means a stale key alone can
    // never make this look orphaned while a more durable identifier (the
    // group, or the parent's variant id) still matches.
    if (group && mainItems.some((item) => String((item.properties || {})._VrvGiftWrapGroup || '') === group)) {
      return true;
    }
    if (parentKey && mainItems.some((item) => String(item.key || '') === parentKey)) {
      return true;
    }
    if (parentVariant && mainItems.some((item) => Number(item.variant_id) === parentVariant)) {
      return true;
    }
    if (group || parentKey || parentVariant) {
      return false;
    }
    // Legacy wraps carry no identifying properties at all. Keep them only
    // while at least one real product exists; this also repairs existing
    // orphan carts predating this property scheme.
    return mainItems.length > 0;
  }

  function getCartSectionRequest() {
    const sections = [];
    const owners = [
      document.querySelector('cart-drawer-items'),
      document.querySelector('cart-items')
    ];
    owners.forEach((owner) => {
      if (!owner || typeof owner.getSectionsToRender !== 'function') return;
      owner.getSectionsToRender().forEach((entry) => {
        const id = entry && (entry.section || entry.id);
        if (id && !sections.includes(id)) sections.push(id);
      });
    });
    return sections.length
      ? { sections, sections_url: window.location.pathname }
      : {};
  }

  function requestGiftWrapDependencySync() {
    giftDependencySyncRequested = true;
    if (giftDependencySyncPromise) return giftDependencySyncPromise;
    giftDependencySyncPromise = (async () => {
      try {
        while (giftDependencySyncRequested) {
          giftDependencySyncRequested = false;
          const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('Could not inspect Gift Wrap cart state.');
          const cart = await response.json();
          giftDependencyLatestCart = cart;
          const mainItems = (cart.items || []).filter((item) => !isGiftWrapCartItem(item));
          const updates = {};
          (cart.items || []).filter(isGiftWrapCartItem).forEach((gift) => {
            updates[gift.key] = giftWrapHasParent(gift, mainItems) ? 1 : 0;
          });
          const needsUpdate = (cart.items || []).filter(isGiftWrapCartItem).some((gift) => {
            return updates[gift.key] === 0 || Number(gift.quantity) !== 1;
          });
          if (!needsUpdate) continue;
          const updateResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(Object.assign({ updates }, getCartSectionRequest()))
          });
          if (!updateResponse.ok) throw new Error('Could not repair Gift Wrap cart state.');
          const updatedCart = await updateResponse.json();
          giftDependencyLatestCart = updatedCart;
          giftDependencyRepairVersion += 1;
          if (typeof window.vrvInvalidateCartJsCache === 'function') window.vrvInvalidateCartJsCache();
          if (typeof window.vrvSyncCartPricingUI === 'function') window.vrvSyncCartPricingUI(updatedCart);
        }
        return giftDependencyLatestCart;
      } finally {
        giftDependencySyncPromise = null;
      }
    })();
    return giftDependencySyncPromise;
  }

  window.vrvEnsureGiftWrapDependencies = requestGiftWrapDependencySync;
  const previousBeforeGiftWrapCartRender = window.vrvBeforeCartRender;
  window.vrvBeforeCartRender = async function (cartMutationResponse) {
    const response = typeof previousBeforeGiftWrapCartRender === 'function'
      ? await previousBeforeGiftWrapCartRender(cartMutationResponse)
      : cartMutationResponse;
    const repairVersionBefore = giftDependencyRepairVersion;
    const confirmedCart = await requestGiftWrapDependencySync();
    // The dependency repair may have issued a second Shopify cart mutation.
    // Always render that confirmed response (including freshly requested
    // sections), never the pre-repair response that still contains an orphan.
    // Preserve the original cart mutation response (and its rendered
    // sections) when dependency state was already correct. Replacing it with
    // plain /cart.js forced a second full drawer request and caused filled ->
    // empty -> filled flashes. Only substitute the repair response when this
    // call actually changed Shopify's cart.
    return giftDependencyRepairVersion !== repairVersionBefore
      ? (confirmedCart || response)
      : response;
  };

  function renderRepairedCartSections(cart) {
    if (!cart || !cart.sections) return;
    Object.keys(cart.sections).forEach((sectionId) => {
      const current = document.getElementById('shopify-section-' + sectionId);
      const html = cart.sections[sectionId];
      if (!current || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById('shopify-section-' + sectionId);
      current.innerHTML = replacement ? replacement.innerHTML : html;
    });
    if (typeof window.vrvBindDrawerExtras === 'function') {
      window.vrvBindDrawerExtras({ skipAutoApply: true });
    }
  }

  function syncGiftWrapDependenciesAndRender() {
    const before = giftDependencyRepairVersion;
    return requestGiftWrapDependencySync().then((cart) => {
      if (giftDependencyRepairVersion !== before) renderRepairedCartSections(cart);
      return cart;
    });
  }

  // A drawer re-render (source 'refreshDrawer' / 'vrv-partial-refresh') shows
  // the cart as it already is -- nothing changed, so don't re-inspect it.
  function isPassiveCartUpdate(event) {
    const source = event && event.detail && event.detail.source;
    return source === 'refreshDrawer' || source === 'vrv-partial-refresh';
  }

  document.addEventListener('cart:updated', (event) => {
    if (isPassiveCartUpdate(event)) return;
    syncGiftWrapDependenciesAndRender().catch((error) => console.warn('[VRV gift wrap dependency]', error));
  });
  window.addEventListener('pageshow', () => {
    syncGiftWrapDependenciesAndRender().catch((error) => console.warn('[VRV gift wrap dependency]', error));
  });
  syncGiftWrapDependenciesAndRender().catch((error) => console.warn('[VRV gift wrap dependency]', error));

  /**
   * FIX (root cause of "after removing Gift Wrap from the cart, the checkbox
   * on the product profile stays checked"): the PDP checkbox's checked state
   * was only ever set by the customer clicking it -- nothing reset it when
   * the gift wrap line was later removed from the cart drawer/page, since
   * add-to-cart happens over AJAX and the PDP is never reloaded. Whenever the
   * cart changes, check whether a gift-wrap line is still actually present
   * and un-check the box(es) if not.
   */
  function syncGiftWrapCheckboxesWithCart(cart) {
    const boxes = document.querySelectorAll('[data-vrv-gift-wrap]');
    if (!boxes.length) return;

    const apply = (cartData) => {
      const items = (cartData && cartData.items) || [];
      const stillPresent = items.some(
        (item) => item && (item.properties && item.properties.VrvGiftWrap != null || /gift\s*wrap/i.test(item.title || ''))
      );
      if (!stillPresent) {
        boxes.forEach((b) => {
          // Only sync if user hasn't explicitly checked it on PDP within last 5 seconds
          if (b.dataset.vrvUserCheckedAt && (Date.now() - Number(b.dataset.vrvUserCheckedAt)) < 5000) return;
          b.checked = false;
        });
      }
    };

    if (cart && Array.isArray(cart.items)) {
      apply(cart);
      return;
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(apply)
      .catch(() => {});
  }

  document.addEventListener('change', (e) => {
    if (e.target && e.target.matches('[data-vrv-gift-wrap]')) {
      if (e.target.checked) {
        e.target.dataset.vrvUserCheckedAt = String(Date.now());
      } else {
        delete e.target.dataset.vrvUserCheckedAt;
      }
    }
  });

  document.addEventListener('cart:updated', (e) => {
    if (isPassiveCartUpdate(e)) return;
    syncGiftWrapCheckboxesWithCart(e && e.detail);
  });

  window.vrvAddProductWithGiftWrap = function (options) {
    return window.vrvGiftWrapCart.addMainAndGiftWrap(options);
  };
})();
